import React, { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Divider,
  Typography,
  LinearProgress,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import CloseIcon from "@mui/icons-material/Close";
import { CRIMSON_RED } from "../../../../enums/Colors";
import BigCalendar from "react-big-calendar-like-google";
import { useTranslation } from "react-i18next";
import { useGetAppointmentsByDate } from "../../../../hooks/useGetAppointments";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import dayjs from "dayjs";
import { APPOINTEMENT_DETAILS } from "../../../../enums/UrlPaths";
import { useNavigate } from "react-router-dom";
import useUser from "../../../../hooks/useUser";
import {
  FaDog,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaStickyNote,
  FaUser,
} from "react-icons/fa";
import UserHint from "../../../../components/display/UserHint";
import moment from "moment";
import { getDateObject } from "../../../../utils/DateTimeRepository";
import { STATUS } from "../../../../enums/LocalStorageValues";
import { STATUSES } from "../../../../enums/AppointmentStatus";
import { useIsMobile } from "../../../../utils/Functions";
import logger from "../../../../utils/logger";

const drawerWidth = 500;
const mobileDrawerWidth="90%";

const formats = {
  timeGutterFormat: (date, culture, localizer) =>
    localizer.format(date, "HH:mm", culture),
};

function serviceTranslation(service, currentLang = "en") {
  if (service?.translations?.[currentLang]) {
    return service.translations[currentLang];
  }
  return service?.title || "";
}

function parseCalendarAppointments(booking, service) {
  if (booking.startDate !== null) {
    return {
      id: booking.id,
      pet: booking.petName ?? null,
      title: service,
      start: dayjs(getDateObject(booking?.startDate)).toDate(),
      end: dayjs(getDateObject(booking?.endDate)).toDate(),
      type: service,
      desc: booking.description,
      status: booking.status,
      pendingMessage:
        String(booking?.status).toUpperCase() == "WAITING_CLINIC" ||
        String(booking?.status).toUpperCase() == "WAITING_USER"
          ? "AppointmentNeedsToBeConfirmed"
          : null,
      owner: booking?.owner ?? null,
    };
  }
}

function getCalendarAppointments(data, lang) {
  if (!data || data == undefined) {
    return new Array();
  }
  const filter = data?.filter(
    (app) =>
      app.status == STATUSES.ACTIVE ||
      app.status == STATUSES.WAITING_CLINIC ||
      app.status == STATUSES.WAITING_USER
  );
  return filter.map((booking) =>
    parseCalendarAppointments(
      booking,
      serviceTranslation(booking?.priceList, lang)
    )
  );
}

export default function Calendar({ startDate, id }) {
  const { preference } = useUser();
  const view = preference?.defaultCalendarView
    ? preference?.defaultCalendarView
    : "agenda";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const parsedStartDate = dayjs(startDate, "DD/MM/YYYY");

  if (!parsedStartDate.isValid()) {
    logger.error("Invalid date:", startDate);
  }

  const [date, setDate] = useState(parsedStartDate.toDate());
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const { data, isLoading, error } = useGetAppointmentsByDate(startDate, open);
  const { mode } = React.useContext(ThemeContext);
  const handleOpenComponent = () => {
    setOpen(true);
  };
  const isMobile=useIsMobile();
  const getDrawerWidth=isMobile ? mobileDrawerWidth :drawerWidth

  const appointments = getCalendarAppointments(data, currentLang);

  const handleDateChange = (newDate) => {
    setDate(newDate);
  };

  const CustomAgendaEvent = ({ event }) => (
    <div className="agenda-event-wrapper">
      {/* Status Badge + Details Icon */}
      <div className="header-row">
        <div className={`status-badge ${String(event.status).toLowerCase()}`}>
          {t(event.status)}
        </div>
      </div>
      <div className="header-row">
        <UserHint hint={event.pendingMessage} />
      </div>

      {/* Pet */}
      {event.pet && (
        <div className="event-item">
          <FaDog className="event-icon" />
          <span>
            {t("Pet")}: <strong>{event.pet}</strong>
          </span>
        </div>
      )}

      {/* Owner */}
      {event.owner && (
        <div className="event-item">
          <FaUser className="event-icon" />
          <span>
            {t("Owner")}: <strong>{event.owner}</strong>
          </span>
        </div>
      )}

      {/* Service */}
      <div className="event-item">
        <FaInfoCircle className="event-icon" />
        <span>
          {t("Service")}: <strong>{event.title}</strong>
        </span>
      </div>

      {/* Description */}
      {event.desc && (
        <>
          <div className="event-item">
            <FaStickyNote className="event-icon" />
            <span>{t("Description")}:</span>
          </div>
          <p className="event-desc">{event.desc}</p>
        </>
      )}
    </div>
  );

  const CustomAgendaTime = ({ event }) => (
    <>
      <div>
        <div>
          {moment(event.start).format("DD")}{" "}
          {String(moment(event.start).format("MMM")).toUpperCase()}
        </div>
      </div>
      <div>
        <span>
          {moment(event.start).format("HH:mm")} -{" "}
          {moment(event.end).format("HH:mm")}
        </span>
      </div>
    </>
  );

  return (
    <>
      <IconButton
        title={t("ShowAllAppointmentsForDate")}
        onClick={() => handleOpenComponent()}
        data-testid={`appointment.pending-card.${id}.show-more-button`}
      >
        <EventIcon />
      </IconButton>
      {/* Right Drawer */}
      <Drawer
        sx={{
          width: getDrawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: getDrawerWidth,
            boxSizing: "border-box",
          },
        }}
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2,
          }}
        >
          <Typography variant="h6">{t("Calendar")}</Typography>
          <IconButton
            onClick={() => setOpen(false)}
            title={t("Close")}
            sx={{
              color: CRIMSON_RED,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        {isLoading ? (
          <LinearProgress />
        ) : error ? (
          <Box sx={{ padding: 2 }}>
            <SimpleAlert
              severity={"error"}
              message={"ErrorFetchingAppointmentsForDate"}
            />
          </Box>
        ) : (
          <div className={"pending-calendar-" + mode}>
            <BigCalendar
              defaultView={view}
              views={[view]}
              date={date}
              events={appointments}
              onNavigate={handleDateChange}
              startAccessor="start"
              endAccessor="end"
              step={30}
              timeslots={2}
              onSelectEvent={(event) => {
                navigate(APPOINTEMENT_DETAILS + event?.id);
              }}
              components={{
                agenda: {
                  date: () => null,
                  time: CustomAgendaTime,
                  event: CustomAgendaEvent,
                },
              }}
              selectable={true}
              onSelecting={(slot) => false}
              scrollToTime={new Date(date)}
              formats={formats}
            />
          </div>
        )}
      </Drawer>
    </>
  );
}
